/*──────────────────────────────────────────────────────────────
 * src/pages/HomePage.jsx – regenerated 2025-07-25
 *   • HOURS MODAL: duplicated wrapper removed; now uses startDate/endDate
 *     state with a simple updateRange() helper and hoursLoading flag.
 *   • EXPLORER MODAL & BUTTON: rendered only when perms.hasOgFiles === true.
 *   • Safer localStorage perms parse (defaults to {} instead of "false").
 *   • Everything else remains line-for-line identical.
 *──────────────────────────────────────────────────────────────*/

import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, useUser } from "ogcommon";
import debounce from "lodash.debounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEye,
  faEyeSlash,
  faClock,
  faFolderOpen,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import ExplorerModal from "../components/ExplorerModal";
import { baseUrl } from "./config";

/* Ticket row (lazy) */
const TicketItem = lazy(() => import("./TicketItem"));

/*──────────────── constants ─────────────────────────────────*/
const LS = {
  SHOW_UNBILLED: "hpShowUnbilled",
  QUICK_FILTER: "hpQuickFilter",
  PAGE: "hpCurrentPage",
  TICKETS_CACHE: "hpTicketsCache",
  START_DATE: "hpStartDate",
  END_DATE: "hpEndDate",
};
const ticketsPerPage = 5;
const toISO = (d) => d.toISOString().slice(0, 10);
const todayISO = () => toISO(new Date());
const sevenDaysAgoISO = () => toISO(new Date(Date.now() - 6 * 86_400_000));
const monthStartISO = () => {
  const d = new Date();
  d.setDate(1);
  return toISO(d);
};

/*──────────────── generic modal (for hours) ─────────────────*/
const Modal = ({ open, onClose, children, theme }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        className={`relative w-full max-w-3xl mx-auto rounded-2xl shadow-2xl overflow-hidden ${
          theme === "dark" ? "bg-gray-800" : "bg-white"
        }`}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-300 hover:text-white text-2xl focus:outline-none z-50"
          aria-label="Close"
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
};

/*──────────────── HomePage ─────────────────────────────────*/
const HomePage = () => {
  /* context & routing */
  const { theme } = useTheme();
  const { userRole, userID, companyName } = useUser();
  const navigate = useNavigate();

  /*──────── permissions ─────────*/
  // Load the full credentials object from localStorage
  const storedCredentials = JSON.parse(
    localStorage.getItem("credentials") || "{}"
  );

  // Extract whatever perms shape you have in credentials.perms
  const { perms: storedPerms = {} } = storedCredentials;

  // Initialize a flat “perms” state (with your defaults) if you still need it
  const [perms, setPerms] = useState(() => ({
    hasOgFiles: false,
    UseFieldTicket: false,
    ...storedPerms,
  }));

  // Initialize writeAccess directly from credentials.perms.ogfieldticket.write
  const [writeAccess, setWriteAccess] = useState(
    () => !!storedCredentials.perms?.ogfieldticket?.write
  );

  /*──────── ticket state ───────*/
  const [allTickets, setAllTickets] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(
    Number(localStorage.getItem(LS.PAGE)) || 1
  );
  const [showUnbilled, setShowUnbilled] = useState(
    JSON.parse(localStorage.getItem(LS.SHOW_UNBILLED) || "true")
  );
  const [searchQuery, setSearchQuery] = useState(
    localStorage.getItem(LS.QUICK_FILTER) || ""
  );
  const [highestTicketNumber, setHighestTicketNumber] = useState(null);

  /*──────── hours modal ───────*/
  const [startDate, setStartDate] = useState(
    localStorage.getItem(LS.START_DATE) || sevenDaysAgoISO()
  );
  const [endDate, setEndDate] = useState(
    localStorage.getItem(LS.END_DATE) || todayISO()
  );
  const [hoursData, setHoursData] = useState([]);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [hoursLoading, setHoursLoading] = useState(false);

  /*──────── explorer modal ─────*/
  const [explorerOpen, setExplorerOpen] = useState(false);

  /*──────── helper: updateRange ─*/
  const updateRange = (sIso, eIso) => {
    setStartDate(sIso);
    setEndDate(eIso);
    localStorage.setItem(LS.START_DATE, sIso);
    localStorage.setItem(LS.END_DATE, eIso);
  };

  /*──────── tickets fetch logic ─*/
  const debouncedSearchTickets = useMemo(
    () =>
      debounce((q) => {
        setSearchQuery(q);
        localStorage.setItem(LS.QUICK_FILTER, q);
        setCurrentPage(1);
      }, 300),
    []
  );

  const fetchTickets = useCallback(async () => {
    try {
      let nextId = null;
      let fetched = [];
      if (navigator.onLine) {
        const res = await fetch(`${baseUrl}/api/tickets.php?withItems=1`);
        const dataRes = await res.json();
        const cleaned = dataRes.filter((t) => {
          if (t.isNextTicketID) {
            nextId = t.Ticket;
            return false;
          }
          return true;
        });
        cleaned.sort(
          (a, b) =>
            b.Ticket - a.Ticket ||
            new Date(b.TicketDate) - new Date(a.TicketDate)
        );
        fetched = cleaned;
        localStorage.setItem(LS.TICKETS_CACHE, JSON.stringify(fetched));
        localStorage.setItem("hpTicketsCacheTs", Date.now().toString());

        // Clean up offline-stored tickets that the server now has
        const serverTicketIds = new Set(fetched.map((t) => String(t.Ticket)));
        const offlineTickets = JSON.parse(localStorage.getItem("tickets") || "[]");
        const stillPending = offlineTickets.filter(
          (t) => !serverTicketIds.has(String(t.Ticket))
        );
        localStorage.setItem("tickets", JSON.stringify(stillPending));
      } else {
        const cacheTs = Number(localStorage.getItem("hpTicketsCacheTs") || "0");
        const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
        if (Date.now() - cacheTs < MAX_CACHE_AGE_MS) {
          fetched = JSON.parse(localStorage.getItem(LS.TICKETS_CACHE) || "[]");
        } else {
          // Stale offline cache — discard to prevent showing ancient tickets
          fetched = [];
        }
      }

      // Merge any offline-stored tickets that aren't yet in the fetched list
      const offlineTickets = JSON.parse(localStorage.getItem("tickets") || "[]");
      if (offlineTickets.length > 0) {
        const fetchedIds = new Set(fetched.map((t) => String(t.Ticket)));
        const pendingTickets = offlineTickets.filter(
          (t) => !fetchedIds.has(String(t.Ticket))
        );
        if (pendingTickets.length > 0) {
          fetched = [...pendingTickets, ...fetched];
        }
      }

      if (!nextId && fetched.length)
        nextId = (Math.max(...fetched.map((t) => +t.Ticket)) + 1).toString();
      setHighestTicketNumber(nextId);
      setAllTickets(fetched);

      const visible = fetched.filter((t) => {
        const unbilled = t.Billed !== "Y";
        const isMe = t.UserID === userID;
        const roleOK = userRole !== "P" || isMe;
        const billOK =
          userRole === "P" ? unbilled : showUnbilled ? unbilled : !unbilled;
        return roleOK && billOK;
      });

      const final = searchQuery
        ? visible.filter((t) =>
            Object.values(t).some((v) =>
              String(v).toLowerCase().includes(searchQuery.toLowerCase())
            )
          )
        : visible;

      setTickets(final);
      setLoading(false);
    } catch (err) {
      console.error("fetchTickets", err);
      setLoading(false);
    }
  }, [userID, userRole, showUnbilled, searchQuery]);

  useEffect(() => {
    fetchTickets();
    window.addEventListener("online", fetchTickets);
    return () => window.removeEventListener("online", fetchTickets);
  }, [fetchTickets]);
  
  useEffect(() => {
    if (hoursOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [hoursOpen]);

  /*─── HOURS: match TicketGrid behavior exactly (clear on range change) ───*/
  useEffect(() => {
    setHoursData([]);
    setHoursLoading(false);
  }, [startDate, endDate]);

  /*──────── hours calc (TicketGrid logic) ─────────*/
  const calcHours = useCallback(() => {
    setHoursLoading(true);
    const startD = new Date(`${startDate}T00:00:00`);
    const endD = new Date(`${endDate}T23:59:59`);
    const map = new Map();

    allTickets.forEach((t) => {
      const td = new Date(`${t.TicketDate}T00:00:00`);
      if (td < startD || td > endD) return;

      t.Items?.forEach((it) => {
        if (it.UseStartStop !== "Y") return;
        const qty = parseFloat(it.Quantity) || 0;
        const id = String(it.ItemID ?? "");
        if (!map.has(id)) {
          map.set(id, {
            ItemID: id,
            ItemDescription: it.ItemDescription,
            HoursForDateRange: qty,
          });
        } else {
          map.get(id).HoursForDateRange += qty;
        }
      });
    });

    setHoursData(
      Array.from(map.values()).sort(
        (a, b) => b.HoursForDateRange - a.HoursForDateRange
      )
    );
    setHoursLoading(false);
  }, [allTickets, startDate, endDate]);

  /*──────── pagination ─────────*/
  const pagedTickets = useMemo(() => {
    const start = (currentPage - 1) * ticketsPerPage;
    return tickets.slice(start, start + ticketsPerPage);
  }, [tickets, currentPage]);

  const pageCount = Math.max(1, Math.ceil(tickets.length / ticketsPerPage));

  /*───────────────────────────── JSX ─────────────────────────*/
  return (
    <div
      className={`min-h-screen ${
        theme === "dark" ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      {/* gradient bg */}
      <div
        className={`fixed inset-0 -z-10 opacity-50 ${
          theme === "dark"
            ? "bg-gradient-to-br from-gray-900 to-gray-800"
            : "bg-gradient-to-br from-gray-100 to-gray-200"
        }`}
      />

      {/*──────── Hours Modal ─────*/}
      <Modal open={hoursOpen} onClose={() => setHoursOpen(false)} theme={theme}>
        <div
          className={`
              w-full max-w-[720px] max-h-[80vh]
              rounded-3xl shadow-2xl ring-1 ring-black/10 backdrop-blur-md
              ${theme === "dark" ? "bg-gray-800/80 text-white" : "bg-white/80 text-gray-900"}
              flex flex-col overflow-hidden
            `}
        >

          {/* header */}
          <header
            className={`
        flex items-center justify-between px-6 py-4
        ${
          theme === "dark"
            ? "bg-gradient-to-r from-indigo-800/90 to-purple-900/90"
            : "bg-gradient-to-r from-indigo-500/90 to-purple-600/90"
        }
        text-white
      `}
          >
            <h2 className="text-xl font-bold tracking-wide">Hours by Item</h2>
            <button
              onClick={() => setHoursOpen(false)}
              className="p-2 transition hover:scale-110 focus:outline-none"
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} /> {/* add faTimes import */}
            </button>
          </header>

          {/* body */}
          <section className="flex-grow overflow-auto p-6 space-y-6">
            {/* quick links */}
            <div className="flex flex-wrap justify-center gap-3">
              {[
                ["Today", 0],
                ["Last 7 d", 6],
                ["MTD", new Date().getDate() - 1],
                ["Prev Month", "prev"],
                [
                  "YTD",
                  (() => {
                    const d = new Date();
                    return Math.floor(
                      (d - new Date(d.getFullYear(), 0, 1)) / 8.64e7
                    );
                  })(),
                ],
              ].map(([label, offset]) => (
                <button
                  key={label}
                  onClick={() => {
                    const now = new Date();
                    let s,
                      e = now;
                    if (offset === "prev") {
                      s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                      e = new Date(now.getFullYear(), now.getMonth(), 0);
                    } else {
                      s = new Date(now);
                      s.setDate(now.getDate() - offset);
                    }
                    updateRange(toISO(s), toISO(e));
                  }}
                  className={`
              px-4 py-1.5 rounded-full font-medium text-sm shadow
              ring-1 ring-inset transition
              ${
                theme === "dark"
                  ? "bg-gray-700/60 hover:bg-gray-600/70 ring-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 ring-gray-300"
              }
            `}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* date pickers + run */}
            <div className="flex flex-wrap justify-center gap-4">
              {[
                ["Start", startDate, (v) => updateRange(v, endDate)],
                ["End", endDate, (v) => updateRange(startDate, v)],
              ].map(([label, val, onChange]) => (
                <label key={label} className="flex flex-col text-sm gap-1">
                  <span className="font-medium">{label}</span>
                  <input
                    type="date"
                    value={val}
                    onChange={(e) => onChange(e.target.value)}
                    className={`
                rounded-md px-3 py-1 shadow-inner
                ${theme === "dark" ? "bg-gray-700" : "bg-gray-200"}
              `}
                  />
                </label>
              ))}

              <button
                onClick={calcHours}
                className={`
            inline-flex items-center px-4 py-2 rounded-md font-semibold shadow
            transition hover:brightness-110
            ${
              theme === "dark"
                ? "bg-purple-700 text-white"
                : "bg-purple-600 text-white"
            }
          `}
              >
                Run
              </button>
            </div>

            {/* results */}
            <div className="max-h-60 overflow-y-auto rounded-lg shadow-inner">
              {hoursLoading ? (
                <p className="italic text-center py-10">Calculating…</p>
              ) : hoursData.length ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr
                      className={`
                  text-left uppercase tracking-wider text-xs
                  ${theme === "dark" ? "text-gray-300" : "text-gray-500"}
                `}
                    >
                      <th className="py-2 pr-2">Item ID</th>
                      <th className="py-2 pr-2">Description</th>
                      <th className="py-2 text-right">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hoursData.map((r, i) => (
                      <tr
                        key={r.ItemID}
                        className={i % 2 ? "bg-black/5 dark:bg-white/5" : ""}
                      >
                        <td className="py-1.5 pr-2">{r.ItemID}</td>
                        <td className="py-1.5 pr-2">{r.ItemDescription}</td>
                        <td className="py-1.5 text-right">
                          {(+r.HoursForDateRange).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="italic text-center py-10">
                  No data for selected range.
                </p>
              )}
            </div>
          </section>

          {/* footer */}
          <footer className="px-6 py-4">
            <button
              onClick={() => setHoursOpen(false)}
              className={`
          w-full rounded-md py-2 font-semibold shadow
          transition hover:brightness-110
          ${
            theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 text-gray-800"
          }
        `}
            >
              Close
            </button>
          </footer>
        </div>
      </Modal>

      {/*──────── Explorer Modal ──*/}
      {perms.hasOgFiles && (
        <ExplorerModal
          open={explorerOpen}
          onClose={() => setExplorerOpen(false)}
          theme={theme}
          companyName={companyName}
        />
      )}

      {/*──────── main card ───────*/}
      <div className="container mx-auto px-4 py-8 relative z-10">
        {loading ? (
          <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-24 w-24 border-t-2 border-b-2 border-blue-500" />
          </div>
        ) : (
          <div className="flex flex-col items-center pt-10">
            <div
              className={`w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden ${
                theme === "dark"
                  ? "bg-gradient-to-r from-gray-800 to-gray-900"
                  : "bg-gradient-to-r from-white to-gray-100"
              }`}
            >
              {/* banner */}
              <div
                className={`p-6 text-center relative ${
                  theme === "dark"
                    ? "bg-gradient-to-r from-blue-900 to-indigo-900"
                    : "bg-gradient-to-r from-blue-500 to-indigo-500"
                }`}
              >
                <h2 className="text-4xl md:text-3xl font-bold mb-6 text-white">
                  Ticket Dashboard
                </h2>

                {/* hours */}
                {["A", "O"].includes(userRole) && (
                  <button
                    onClick={() => {
                      setHoursOpen(true);
                    }}
                    className="absolute top-4 left-4 text-white hover:text-gray-200 focus:outline-none"
                    title="Hours summary"
                  >
                    <FontAwesomeIcon icon={faClock} size="lg" />
                  </button>
                )}

                {/* explorer */}
                {perms.hasOgFiles && (
                  <button
                    onClick={() => setExplorerOpen(true)}
                    className="absolute top-4 left-14 text-white hover:text-gray-200 focus:outline-none"
                    title="Lease File Explorer"
                  >
                    <FontAwesomeIcon icon={faFolderOpen} size="lg" />
                  </button>
                )}

                {/* billed/unbilled */}
                {userRole !== "P" && (
                  <button
                    onClick={() =>
                      setShowUnbilled((prev) => {
                        const n = !prev;
                        localStorage.setItem(
                          LS.SHOW_UNBILLED,
                          JSON.stringify(n)
                        );
                        setCurrentPage(1);
                        return n;
                      })
                    }
                    className={`absolute top-4 right-4 hidden sm:inline-flex items-center font-bold py-2 px-4 rounded-full shadow-lg transform hover:scale-105 transition ${
                      theme === "dark"
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-white hover:bg-gray-100 text-blue-600"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={showUnbilled ? faEye : faEyeSlash}
                      className="mr-2"
                    />
                    {showUnbilled ? "Unbilled" : "Billed"}
                  </button>
                )}

                {/* search tickets */}
                <input
                  type="text"
                  defaultValue={searchQuery}
                  onChange={(e) => debouncedSearchTickets(e.target.value)}
                  placeholder="Search tickets…"
                  className={`w-full max-w-lg mx-auto mb-6 px-4 py-2 rounded-lg shadow-md focus:ring-2 focus:ring-blue-500 ${
                    theme === "dark"
                      ? "bg-gray-800 text-white placeholder-gray-400"
                      : "bg-white text-gray-800 placeholder-gray-500"
                  }`}
                />

                {writeAccess && (
                  <button
                    onClick={() =>
                      navigate(`/create-field-ticket/${highestTicketNumber}`)
                    }
                    className={`inline-flex items-center font-bold py-3 px-6 rounded-full shadow-lg transform hover:scale-105 transition ${
                      theme === "dark"
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-white hover:bg-gray-100 text-blue-600"
                    }`}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Create Ticket
                  </button>
                )}
              </div>

              {/* list */}
              <Suspense fallback={<div className="p-4">Loading…</div>}>
                <ul
                  className={`divide-y ${
                    theme === "dark" ? "divide-gray-700" : "divide-gray-300"
                  }`}
                >
                  {pagedTickets.map((t) => (
                    <TicketItem
                      key={t.Ticket}
                      ticket={t}
                      theme={theme}
                      searchQuery={searchQuery}
                      onClick={() =>
                        navigate("/view-field-ticket", {
                          state: { ticket: t, highestTicketNumber },
                        })
                      }
                    />
                  ))}
                </ul>
              </Suspense>

              {/* pagination */}
              <div className="py-6 flex flex-wrap justify-center gap-2">
                {Array.from({ length: pageCount }).map((_, idx) => {
                  const page = idx + 1;
                  const active = page === currentPage;
                  return (
                    <button
                      key={page}
                      onClick={() => {
                        setCurrentPage(page);
                        localStorage.setItem(LS.PAGE, page);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full shadow transition ${
                        active
                          ? theme === "dark"
                            ? "bg-blue-600 text-white"
                            : "bg-blue-500 text-white"
                          : theme === "dark"
                          ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(HomePage);
